import React, { useState, useEffect, useRef } from "react";
import {
	X,
	Clock,
	Users,
	Video,
	MapPin,
	ChevronDown,
	Bell,
	FileText,
	Calendar,
	Bold,
	Italic,
	Underline,
	List,
	ListOrdered,
	Coffee,
	Globe,
	Lock,
} from "lucide-react";

import { format, startOfDay } from "date-fns";

import TimezoneDropdown from "./TimezoneDropdown";
import RepeatDropdown from "./RepeatDropdown";
import TimePickerDropdown from "./TimePicker";

// Import the modal components
import ParticipantsModal from "./ParticipantsModal";
import ConferencingModal from "./ConferencingModal";
import LocationModal from "./LocationModal";
import DocumentsModal from "./DocumentsModal";
import RemindersModal from "./RemindersModal";
import StatusModal from "./StatusModal";
import VisibilityModal from "./VisibilityModal";

/**
 * Prepares event data for Google Calendar API
 * @param {Object} formData - Form data from the component
 * @returns {Object} - Formatted event data for Google Calendar
 */
const prepareGoogleEventData = (formData) => {
	const {
		title,
		startTime,
		endTime,
		date,
		isAllDay,
		timezone,
		repeat,
		participants,
		conferencing,
		location,
		documents,
		description,
		reminders,
		visibility,
		status,
	} = formData;

	console.log("formData", formData);
	// Format date and time based on whether it's an all-day event
	const startDateTime = isAllDay
		? { date: date }
		: {
				dateTime: `${date}T${startTime}:00`,
				timeZone: timezone,
		  };

	const endDateTime = isAllDay
		? { date: date }
		: {
				dateTime: `${date}T${endTime}:00`,
				timeZone: timezone,
		  };

	// Build the complete description
	let fullDescription = description || "";

	// Add conferencing information if available
	if (conferencing?.url) {
		fullDescription += `\n\nMeeting link: ${conferencing.url}`;
	}

	// Format attachments for Google Calendar API
	const attachments = documents.map((doc) => ({
		fileUrl: doc.url,
		title: doc.title,
		mimeType: getMimeType(doc.url),
		iconLink: getIconLink(doc.url),
	}));



	// Create the event object
	const eventData = {
		summary: title || "Untitled Event",
		description: fullDescription,
		start: startDateTime,
		end: endDateTime,
		attendees: Array.isArray(participants)
			? participants.map((email) => ({ email }))
			: [],
		location: location || undefined,
		conferenceData:
			conferencing?.type === "gmeet"
				? {
						createRequest: {
							requestId: crypto.randomUUID(),
							conferenceSolutionKey: { type: "hangoutsMeet" },
						},
				  }
				: undefined,
		visibility: visibility === "default" ? undefined : visibility,
		transparency: status === "free" ? "transparent" : "opaque",
		attachments: attachments.length > 0 ? attachments : undefined,
		supportsAttachments: true,
	};

	console.log("eventData", eventData);

	// Only add recurrence if it's not 'none'
	if (repeat !== "none") {
		eventData.recurrence = [repeat];
	}

	// Only add reminders if they exist
	if (Array.isArray(reminders) && reminders.length > 0) {
		eventData.reminders = {
			useDefault: false,
			overrides: reminders.map((minutes) => ({
				method: "popup",
				minutes: parseInt(minutes),
			})),
		};
	} else {
		eventData.reminders = {
			useDefault: false,
			overrides: [],
		};
	}

	return eventData;
};

const getMimeType = (url) => {
	// Google Workspace
	if (url.includes("docs.google.com/document"))
		return "application/vnd.google-apps.document";
	if (url.includes("docs.google.com/spreadsheets"))
		return "application/vnd.google-apps.spreadsheet";
	if (url.includes("docs.google.com/presentation"))
		return "application/vnd.google-apps.presentation";
	if (url.includes("docs.google.com/forms"))
		return "application/vnd.google-apps.form";

	// Microsoft Office Online
	if (url.includes("office.live.com/word"))
		return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
	if (url.includes("office.live.com/excel"))
		return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
	if (url.includes("office.live.com/powerpoint"))
		return "application/vnd.openxmlformats-officedocument.presentationml.presentation";

	// Common cloud services
	if (url.includes("dropbox.com")) return "application/vnd.dropbox.file";
	if (url.includes("box.com")) return "application/vnd.box.file";
	if (url.includes("onedrive.live.com")) return "application/vnd.onedrive.file";

	// Default for other links
	return "text/uri-list";
};

const getIconLink = (url) => {
	if (url.includes("docs.google.com/document"))
		return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.document";
	if (url.includes("docs.google.com/spreadsheets"))
		return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.spreadsheet";
	if (url.includes("docs.google.com/presentation"))
		return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.presentation";
	if (url.includes("docs.google.com/forms"))
		return "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.form";

	// Default icon for other files
	return "https://drive-thirdparty.googleusercontent.com/16/type/text/uri-list";
};

/**
 * Validates event form data
 * @param {Object} formData - Form data to validate
 * @returns {string[]} - Array of error messages, empty if valid
 */
const validateEventData = (formData) => {
	const errors = [];

	if (!formData.title?.trim()) {
		errors.push("Title is required");
	}

	if (!formData.isAllDay) {
		if (!formData.startTime || !formData.endTime) {
			errors.push("Start and end times are required for non-all-day events");
			return errors;
		}

		try {
			const start = new Date(`${formData.date}T${formData.startTime}`);
			const end = new Date(`${formData.date}T${formData.endTime}`);

			if (isNaN(start.getTime()) || isNaN(end.getTime())) {
				errors.push("Invalid date or time format");
				return errors;
			}

			if (end <= start) {
				errors.push("End time must be after start time");
			}
		} catch (error) {
			console.error("Date validation error:", error);
			errors.push("Invalid date or time format");
		}
	}

	return errors;
};

const CreateEvent = ({ isOpen, onClose, onCreateEvent, initialTime }) => {
	const [formData, setFormData] = useState({
		title: "",
		startTime: format(new Date(), "HH:mm"),
		endTime: format(new Date(Date.now() + 30 * 60000), "HH:mm"),
		date: format(startOfDay(new Date()), "yyyy-MM-dd"),
		isAllDay: false,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		repeat: "none",
		participants: [],
		conferencing: null,
		location: "",
		description: "",
		documents: [],
		reminders: ["30"],
		visibility: "default",
		status: "busy",
		sendUpdates: "all",
		guestPermissions: {
			canModify: false,
			canInviteOthers: true,
			canSeeGuestList: true,
		},
	});

	const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
	const [isRepeatOpen, setIsRepeatOpen] = useState(false);
	const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
	const [isConferencingOpen, setIsConferencingOpen] = useState(false);
	const [isLocationOpen, setIsLocationOpen] = useState(false);
	const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
	const [isRemindersOpen, setIsRemindersOpen] = useState(false);
	const [isStatusOpen, setIsStatusOpen] = useState(false);
	const [isVisibilityOpen, setIsVisibilityOpen] = useState(false);
	const [errors, setErrors] = useState([]);
	const [isInteractionEnabled, setIsInteractionEnabled] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleConferencingChange = (conferenceData) => {
		setFormData((prev) => {
			const newData = {
				...prev,
				conferencing: conferenceData,
			};
			return newData;
		});
	};

	useEffect(() => {
		if (isOpen) {
			setIsInteractionEnabled(false); // Reset on open
			const timer = setTimeout(() => {
				setIsInteractionEnabled(true);
			}, 500); // Increased to 500ms to be safe

			return () => {
				clearTimeout(timer);
				setIsInteractionEnabled(false);
			};
		}
	}, [isOpen]);

	const handleClose = () => {
		if (!isInteractionEnabled) return; // Prevent early closing
		resetForm();
		onClose();
	};

	const handleStatusChange = (newStatus) => {
		setFormData((prev) => ({ ...prev, status: newStatus }));
		setIsStatusOpen(false);
	};

	const handleVisibilityChange = (newVisibility) => {
		setFormData((prev) => ({ ...prev, visibility: newVisibility }));
		setIsVisibilityOpen(false);
	};
	const handleDocumentsChange = (newDocuments) => {
		setFormData((prev) => ({
			...prev,
			documents: newDocuments,
		}));
	};

	const handleRemindersChange = (newReminders) => {
		setFormData((prev) => ({
			...prev,
			reminders: newReminders,
		}));
	};

	const handleParticipantAdd = (email) => {
		setFormData((prev) => ({
			...prev,
			participants: [...prev.participants, email],
		}));
	};

	const handleParticipantChange = (newParticipants) => {
		setFormData((prev) => ({
			...prev,
			participants: newParticipants,
		}));
	};

	const resetForm = () => {
		// Reset form data to initial state
		setFormData({
			title: "",
			startTime: "",
			endTime: "",
			date: format(startOfDay(new Date()), "yyyy-MM-dd"),
			isAllDay: false,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			repeat: "none",
			participants: [],
			conferencing: "",
			location: "",
			description: "",
			documents: [],
			reminders: ["30"],
			visibility: "default",
			status: "busy",
			sendUpdates: "all",
			guestPermissions: {
				canModify: false,
				canInviteOthers: false,
				canSeeGuestList: true,
			},
		});

		// Reset rich text editor content
		if (editorRef.current) {
			editorRef.current.innerHTML = "";
		}

		// Reset all format states
		setActiveFormats({
			bold: false,
			italic: false,
			underline: false,
			insertUnorderedList: false,
			insertOrderedList: false,
		});

		// Reset error states
		setErrors([]);

		// Reset all modal states
		setIsTimezoneOpen(false);
		setIsRepeatOpen(false);
		setIsParticipantsOpen(false);
		setIsConferencingOpen(false);
		setIsLocationOpen(false);
		setIsDocumentsOpen(false);
		setIsRemindersOpen(false);
		setIsStatusOpen(false);
		setIsVisibilityOpen(false);
		setIsRichTextFocused(false);
	};

	useEffect(() => {
		if (!isOpen) {
			resetForm();
		}
	}, [isOpen]);
	const editorRef = useRef(null);
	const [isRichTextFocused, setIsRichTextFocused] = useState(false);
	const [activeFormats, setActiveFormats] = useState({
		bold: false,
		italic: false,
		underline: false,
		insertUnorderedList: false, // Changed from unorderedList
		insertOrderedList: false, // Changed from orderedList
	});
	const dateInputRef = useRef(null);

	const handleDateRowClick = () => {
		if (/Mobi|Android/i.test(navigator.userAgent)) {
			// Focus and click the input directly
			dateInputRef.current?.focus();
			dateInputRef.current?.click();
		} else {
			// On desktop, we can use showPicker()
			dateInputRef.current?.showPicker?.();
		}
	};

	useEffect(() => {
		if (initialTime) {
			const { start, end, date } = initialTime;

			// Format the time strings properly
			setFormData((prev) => ({
				...prev,
				date: format(date, "yyyy-MM-dd"),
				startTime: format(start, "HH:mm"),
				endTime: format(end, "HH:mm"),
			}));
		} else {
			// Set default times if no initialTime is provided
			const now = new Date();
			const thirtyMinutesLater = new Date(now.getTime() + 30 * 60000);

			setFormData((prev) => ({
				...prev,
				date: format(now, "yyyy-MM-dd"),
				startTime: format(now, "HH:mm"),
				endTime: format(thirtyMinutesLater, "HH:mm"),
			}));
		}
	}, [initialTime]);

	const handleFormat = (command) => {
		// Focus the editor if it's not focused
		if (!editorRef.current?.contains(document.activeElement)) {
			editorRef.current?.focus();
		}

		// Special handling for lists
		if (command === "insertUnorderedList" || command === "insertOrderedList") {
			const selection = window.getSelection();
			const editor = editorRef.current;

			// If the editor is empty or just contains a <br>, insert a list item first
			if (editor.innerHTML === "" || editor.innerHTML === "<br>") {
				editor.innerHTML =
					command === "insertUnorderedList"
						? "<ul><li><br></li></ul>"
						: "<ol><li><br></li></ol>";

				// Place cursor inside the list item
				const li = editor.querySelector("li");
				const newRange = document.createRange();
				newRange.setStart(li, 0);
				newRange.collapse(true);
				selection.removeAllRanges();
				selection.addRange(newRange);
			} else {
				// Execute list command on selected text
				document.execCommand(command, false);
			}
		} else {
			// Handle other formatting commands (bold, italic, underline)
			document.execCommand(command, false);
		}

		// Update format state after a short delay to ensure command has completed
		setTimeout(() => {
			setActiveFormats({
				bold: document.queryCommandState("bold"),
				italic: document.queryCommandState("italic"),
				underline: document.queryCommandState("underline"),
				insertUnorderedList: document.queryCommandState("insertUnorderedList"),
				insertOrderedList: document.queryCommandState("insertOrderedList"),
			});
		}, 0);
	};

	const getDisplayDate = () => {
		try {
			const [year, month, day] = formData.date.split("-");

			// Create date using numeric values
			const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

			return format(dateObj, "MM/dd/yyyy");
		} catch (error) {
			console.error("Date formatting error:", error);
			return format(new Date(), "MM/dd/yyyy");
		}
	};

	const getFormatButtonClass = (isActive) => `
    p-1 rounded transition-colors
    ${
			isActive
				? "bg-gray-200 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
				: "hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
		}
  `;

	const ConferencingButton = () => {
		if (formData.conferencing?.url) {
			return (
				<div className="w-full space-y-2">
					<a
						href={formData.conferencing.url}
						target="_blank"
						rel="noopener noreferrer"
						className="w-full flex items-center gap-3 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400 transition-colors group"
					>
						<Video className="w-5 h-5" />
						<div className="flex-1 text-left">
							<div className="text-sm font-medium">Google Meet</div>
							<div className="text-xs text-blue-500/80 dark:text-blue-400/80 truncate">
								{formData.conferencing.url}
							</div>
						</div>
					</a>
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => setIsConferencingOpen(true)}
							className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-lg transition-colors"
						>
							Manage conference settings
						</button>
						<button
							type="button"
							onClick={() => {
								setFormData((prev) => ({
									...prev,
									conferencing: null,
								}));
							}}
							className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
						>
							Remove conference
						</button>
					</div>
				</div>
			);
		}

		return (
			<button
				type="button"
				onClick={() => setIsConferencingOpen(true)}
				className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
			>
				<Video className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
				<span className="text-sm">Add video conferencing</span>
			</button>
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const descriptionElement = editorRef.current;
		const updatedFormData = {
			...formData,
			description: descriptionElement
				? descriptionElement.innerHTML.trim()
				: formData.description,
		};

		const errors = validateEventData(updatedFormData);
		if (errors.length > 0) {
			setErrors(errors);
			return;
		}

		try {
			setIsSubmitting(true);

			if (!updatedFormData.title?.trim()) {
				throw new Error("Title is required");
			}

			if (!updatedFormData.startTime || !updatedFormData.endTime) {
				throw new Error("Start and end times are required");
			}

			const eventData = prepareGoogleEventData(updatedFormData);

			// Add the permissions
			eventData.sendUpdates = updatedFormData.sendUpdates;
			eventData.guestsCanModify = updatedFormData.guestPermissions.canModify;
			eventData.guestsCanInviteOthers =
				updatedFormData.guestPermissions.canInviteOthers;
			eventData.guestsCanSeeOtherGuests =
				updatedFormData.guestPermissions.canSeeGuestList;

			await onCreateEvent(eventData);
			resetForm();
			onClose();
		} catch (error) {
			console.error("Error creating event:", error);
			setErrors([error.message || "Failed to create event. Please try again."]);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<>
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={handleClose}
			/>
			<div
				className={`fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden ${
					isInteractionEnabled ? "" : "pointer-events-none"
				}`}
			>
				<form
					onSubmit={handleSubmit}
					className="flex flex-col h-full max-h-[60vh]"
				>
					{/* Header */}
					<div className="flex items-center justify-between p-4">
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							Create Event
						</h2>
						<button
							type="button"
							//reset and close
							onClick={handleClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
						>
							<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
						</button>
					</div>
					{errors.length > 0 && (
						<div className="px-4 py-2 bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg mb-4">
							{errors.map((error, index) => (
								<div key={index}>{error}</div>
							))}
						</div>
					)}

					{/* Form Content */}
					<div className="flex-1 overflow-y-auto p-4 space-y-6  scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 ">
						{/* Title */}
						<div className="space-y-1">
							<input
								type="text"
								placeholder="Add title"
								className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-gray-200 dark:border-gray-700 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-0 outline-none text-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
								value={formData.title}
								onChange={(e) =>
									setFormData({ ...formData, title: e.target.value })
								}
							/>
						</div>

						{/* Time Selection */}
						<div className="space-y-4">
							<div className="flex items-center gap-4 px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg">
								<Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
								<div className="flex items-center gap-3">
									<TimePickerDropdown
										value={formData.startTime}
										onChange={(time) =>
											setFormData({ ...formData, startTime: time })
										}
										isStart={true}
									/>
									<span className="text-gray-500 dark:text-gray-400">-</span>
									<TimePickerDropdown
										value={formData.endTime}
										onChange={(time) =>
											setFormData({ ...formData, endTime: time })
										}
										isStart={false}
										startTime={formData.startTime}
									/>
								</div>
							</div>
							<div className="relative">
								<button
									type="button"
									onClick={handleDateRowClick}
									className="w-full flex items-center gap-4 px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors"
								>
									<Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									<span className="text-gray-900 dark:text-gray-100">
										{getDisplayDate()}
									</span>
								</button>
								<input
									ref={dateInputRef}
									type="date"
									required
									className="absolute left-0 top-full opacity-0 dark:[color-scheme:dark] [color-scheme:light]"
									value={formData.date}
									onChange={(e) => {
										const selectedDate = e.target.value;
										if (selectedDate) {
											setFormData((prev) => ({
												...prev,
												date: selectedDate,
											}));
										} else {
											setFormData((prev) => ({
												...prev,
												date: format(new Date(), "yyyy-MM-dd"),
											}));
										}
									}}
								/>
							</div>
						</div>

						{/* Quick Options */}
						<div className="flex items-center gap-2 px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg">
							<span className="text-sm text-gray-600 dark:text-gray-300">
								All-day
							</span>
							<div className="flex-1" />
							<label className="relative inline-flex items-center cursor-pointer">
								<input
									type="checkbox"
									className="sr-only peer"
									checked={formData.isAllDay}
									onChange={(e) =>
										setFormData({ ...formData, isAllDay: e.target.checked })
									}
								/>
								<div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-500"></div>
							</label>
						</div>

						{/* Other Options */}
						<div className="space-y-3">
							<TimezoneDropdown
								value={formData.timezone}
								onChange={(timezone) =>
									setFormData((prev) => ({ ...prev, timezone }))
								}
								isOpen={isTimezoneOpen}
								setIsOpen={setIsTimezoneOpen}
								date={formData.date}
								startTime={formData.startTime}
								endTime={formData.endTime}
							/>

							<RepeatDropdown
								value={formData.repeat}
								onChange={(repeat) =>
									setFormData((prev) => ({ ...prev, repeat }))
								}
								isOpen={isRepeatOpen}
								setIsOpen={setIsRepeatOpen}
							/>

							<button
								type="button"
								onClick={() => setIsParticipantsOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<Users className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.participants.length > 0
										? `${formData.participants.length} participant${
												formData.participants.length > 1 ? "s" : ""
										  }`
										: "Add participants"}
								</span>
							</button>

							<ConferencingButton />

							{isConferencingOpen && (
								<ConferencingModal
									conferencing={formData.conferencing}
									onChange={handleConferencingChange}
									onClose={() => setIsConferencingOpen(false)}
									eventTitle={formData.title}
								/>
							)}

							<button
								type="button"
								onClick={() => setIsLocationOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.location || "Add location"}
								</span>
							</button>

							{isLocationOpen && (
								<LocationModal
									location={formData.location}
									onChange={(newLocation) => {
										setFormData((prev) => ({ ...prev, location: newLocation }));
										setIsLocationOpen(false);
									}}
									onClose={() => setIsLocationOpen(false)}
								/>
							)}

							<button
								type="button"
								onClick={() => setIsDocumentsOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<FileText className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.documents.length > 0
										? `${formData.documents.length} attachment${
												formData.documents.length > 1 ? "s" : ""
										  }`
										: "Add attachments"}
								</span>
							</button>

							{isDocumentsOpen && (
								<DocumentsModal
									documents={formData.documents}
									onChange={handleDocumentsChange}
									onClose={() => setIsDocumentsOpen(false)}
								/>
							)}
							{/* Add the modals at the end */}
							{isParticipantsOpen && (
								<ParticipantsModal
									participants={formData.participants}
									onAdd={handleParticipantAdd}
									onChange={handleParticipantChange}
									onClose={() => setIsParticipantsOpen(false)}
									onSendUpdatesChange={(value) => {
										setFormData((prev) => ({
											...prev,
											sendUpdates: value.sendUpdates,
											guestPermissions: {
												canModify: value.guestsCanModify,
												canInviteOthers: value.guestsCanInviteOthers,
												canSeeGuestList: value.guestsCanSeeOtherGuests,
											},
										}));
										setIsParticipantsOpen(false);
									}}
								/>
							)}

							{/* Description with Rich Text */}
							<div className="space-y-2">
								<div
									className={`px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-t-lg flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 ${
										isRichTextFocused
											? "border-blue-500 dark:border-blue-500"
											: ""
									}`}
								>
									<button
										type="button"
										onClick={() => handleFormat("bold")}
										className={getFormatButtonClass(activeFormats.bold)}
										title="Bold"
									>
										<Bold className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleFormat("italic")}
										className={getFormatButtonClass(activeFormats.italic)}
										title="Italic"
									>
										<Italic className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleFormat("underline")}
										className={getFormatButtonClass(activeFormats.underline)}
										title="Underline"
									>
										<Underline className="w-4 h-4" />
									</button>
									<div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
									<button
										type="button"
										onClick={() => handleFormat("insertUnorderedList")}
										className={getFormatButtonClass(
											activeFormats.insertUnorderedList
										)} // Changed from unorderedList
										title="Bullet List"
									>
										<List className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleFormat("insertOrderedList")}
										className={getFormatButtonClass(
											activeFormats.insertOrderedList
										)} // Changed from orderedList
										title="Numbered List"
									>
										<ListOrdered className="w-4 h-4" />
									</button>
								</div>
								<div
									ref={editorRef}
									id="rich-text-editor"
									contentEditable
									className="min-h-[100px] px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-b-lg 
									text-gray-900 dark:text-gray-100 focus:outline-none 
									[&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 
									[&>ul>li]:mb-1 [&>ol>li]:mb-1 
									[&>ul>li]:pl-1 [&>ol>li]:pl-1
									empty:before:content-[attr(placeholder)] 
									empty:before:text-gray-400 
									dark:empty:before:text-gray-500
									[&_li]:min-h-[1.5em]"
									onFocus={() => {
										setIsRichTextFocused(true);
										// Check current formatting on focus
										setActiveFormats({
											bold: document.queryCommandState("bold"),
											italic: document.queryCommandState("italic"),
											underline: document.queryCommandState("underline"),
											insertUnorderedList: document.queryCommandState(
												"insertUnorderedList"
											),
											insertOrderedList:
												document.queryCommandState("insertOrderedList"),
										});
									}}
									onBlur={() => setIsRichTextFocused(false)}
									onKeyUp={() => {
										// Update format state when typing
										setActiveFormats({
											bold: document.queryCommandState("bold"),
											italic: document.queryCommandState("italic"),
											underline: document.queryCommandState("underline"),
											insertUnorderedList: document.queryCommandState(
												"insertUnorderedList"
											),
											insertOrderedList:
												document.queryCommandState("insertOrderedList"),
										});
									}}
									onMouseUp={() => {
										// Update format state when selecting text
										setActiveFormats({
											bold: document.queryCommandState("bold"),
											italic: document.queryCommandState("italic"),
											underline: document.queryCommandState("underline"),
											insertUnorderedList: document.queryCommandState(
												"insertUnorderedList"
											),
											insertOrderedList:
												document.queryCommandState("insertOrderedList"),
										});
									}}
									placeholder="Add description"
								/>
							</div>

							{/* Reminders */}
							<button
								type="button"
								onClick={() => setIsRemindersOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-sm text-gray-600 dark:text-gray-300"
							>
								<Bell className="w-4 h-4 text-gray-500 dark:text-gray-400" />
								<span>
									{formData.reminders.length > 0
										? `${formData.reminders.length} reminder${
												formData.reminders.length > 1 ? "s" : ""
										  }`
										: "Add reminder"}
								</span>
								<ChevronDown className="w-4 h-4 ml-auto text-gray-500 dark:text-gray-400" />
							</button>

							<div className="flex gap-2">
								{/* Status Button */}
								<button
									type="button"
									onClick={() => setIsStatusOpen(true)}
									className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors"
								>
									{/* Conditionally render icon based on status */}
									{formData.status === "busy" && (
										<Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.status === "free" && (
										<Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.status === "tentative" && (
										<Coffee className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}

									<span className="text-sm">
										{formData.status || "Select status"}
									</span>
								</button>

								{/* Visibility Button */}
								<button
									type="button"
									onClick={() => setIsVisibilityOpen(true)}
									className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors"
								>
									{/* Conditionally render icon based on visibility */}
									{formData.visibility === "default" && (
										<Globe className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.visibility === "public" && (
										<Users className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.visibility === "private" && (
										<Lock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}

									<span className="text-sm">
										{formData.visibility || "Select visibility"}
									</span>
								</button>
							</div>

							{isRemindersOpen && (
								<RemindersModal
									reminders={formData.reminders}
									onChange={handleRemindersChange}
									onClose={() => setIsRemindersOpen(false)}
								/>
							)}

							{/* Status and Visibility Modals */}
							{isStatusOpen && (
								<StatusModal
									value={formData.status}
									onChange={handleStatusChange}
									onClose={() => setIsStatusOpen(false)}
								/>
							)}

							{isVisibilityOpen && (
								<VisibilityModal
									value={formData.visibility}
									onChange={handleVisibilityChange}
									onClose={() => setIsVisibilityOpen(false)}
								/>
							)}
						</div>
					</div>

					{/* Footer */}
					<div className="p-4 flex justify-end gap-2 border-t border-gray-100 dark:border-gray-800">
						<button
							type="button"
							onClick={handleClose}
							disabled={isSubmitting}
							className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Cancel
						</button>
						<button
							onClick={handleSubmit}
							disabled={isSubmitting}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors relative disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
						>
							{isSubmitting ? (
								<div className="flex items-center justify-center gap-2">
									<svg
										className="animate-spin h-4 w-4 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									<span>Creating...</span>
								</div>
							) : (
								"Create"
							)}
						</button>
					</div>
				</form>
			</div>
		</>
	);
};

export default CreateEvent;
